<template>
  <transition name="upd-fade">
    <div v-if="visible" class="upd-notice" role="status" dir="rtl">
      <span class="upd-notice__dot" aria-hidden="true"></span>
      <span class="upd-notice__text">نسخهٔ جدید پنل آماده است<span v-if="remoteVersion"> (v{{ remoteVersion }})</span></span>
      <button type="button" class="upd-notice__btn" :disabled="reloading" @click="update">به‌روزرسانی</button>
      <button type="button" class="upd-notice__later" @click="dismiss">بعداً</button>
    </div>
  </transition>
</template>

<script>
import { BUILD_ID } from '@/utils/version'

const INTERVAL = 5 * 60 * 1000

export default {
  name: 'UpdateChecker',
  data() {
    return { visible: false, remoteVersion: '', reloading: false, timer: null, lastCheck: 0 }
  },
  mounted() {
    if (BUILD_ID === 'dev') return // local dev: nothing to compare
    this.check()
    this.timer = setInterval(this.check, INTERVAL)
    window.addEventListener('focus', this.onFocus)
    document.addEventListener('visibilitychange', this.onVisibility)
  },
  beforeUnmount() {
    clearInterval(this.timer)
    window.removeEventListener('focus', this.onFocus)
    document.removeEventListener('visibilitychange', this.onVisibility)
  },
  methods: {
    onFocus() { this.check(true) },
    onVisibility() { if (document.visibilityState === 'visible') this.check(true) },
    async check(throttled) {
      const now = Date.now()
      if (throttled && now - this.lastCheck < 15000) return
      this.lastCheck = now
      try {
        const res = await fetch('/version.json?t=' + now, { cache: 'no-store' })
        if (!res.ok) return
        const data = await res.json()
        if (data && data.build && data.build !== BUILD_ID) {
          this.remoteVersion = data.version || ''
          this.visible = true
        }
      } catch (e) { /* offline or missing file: ignore */ }
    },
    dismiss() { this.visible = false }, // re-shown on next check
    async update() {
      if (this.reloading) return
      this.reloading = true
      try {
        if (navigator.serviceWorker && navigator.serviceWorker.getRegistrations) {
          const regs = await navigator.serviceWorker.getRegistrations()
          await Promise.all(regs.map((r) => r.unregister()))
        }
        if (window.caches && caches.keys) {
          const keys = await caches.keys()
          await Promise.all(keys.map((k) => caches.delete(k)))
        }
        // refresh the HTTP-cached index.html so the new asset names are picked up
        await fetch('/index.html', { cache: 'reload' }).catch(() => {})
      } catch (e) { /* ignore */ }
      const url = new URL(window.location.href)
      url.searchParams.set('_v', Date.now().toString(36))
      window.location.replace(url.toString())
    }
  }
}
</script>

<style scoped>
.upd-notice {
  position: fixed; left: 1.5em; bottom: 1.5em; z-index: 10000;
  display: flex; align-items: center; gap: 12px;
  max-width: calc(100vw - 3em); padding: 10px 12px 10px 14px;
  background: var(--surface); color: var(--text); font-family: CustomFont, sans-serif; font-size: 0.875rem;
  border: 1px solid var(--border); border-left: 3px solid var(--accent); border-radius: 8px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
}
.upd-notice__dot { width: 8px; height: 8px; border-radius: 50%; background: var(--accent); flex: 0 0 auto; }
.upd-notice__text { line-height: 1.6; }
.upd-notice__btn {
  background: var(--accent); color: #fff; border: 1px solid var(--accent); border-radius: 6px;
  padding: 5px 14px; font: inherit; font-weight: 600; cursor: pointer; white-space: nowrap;
}
.upd-notice__btn:hover { background: var(--accent-hover); border-color: var(--accent-hover); }
.upd-notice__btn:disabled { opacity: .6; cursor: default; }
.upd-notice__later {
  background: transparent; border: 0; color: var(--muted); font: inherit; cursor: pointer; padding: 4px 2px; white-space: nowrap;
}
.upd-notice__later:hover { color: var(--text); }
.upd-fade-enter-active, .upd-fade-leave-active { transition: opacity .2s ease, transform .2s ease; }
.upd-fade-enter, .upd-fade-leave-to { opacity: 0; transform: translateY(8px); }
</style>